/**
 * PocketBase JS SDK singleton.
 *
 * Reads the base URL from `import.meta.env.VITE_PB_URL`. The SDK persists the
 * auth session in localStorage by default (via authStore), so the user stays
 * signed in across page reloads.
 *
 * Usage:
 *   import { pb, useAuth } from '@/lib/pb';
 *   const docs = await pb.collection('documents').getList(1, 50);
 */

import PocketBase from 'pocketbase';

const PB_URL = import.meta.env.VITE_PB_URL ?? 'https://api.sunsetharborcb.com';

export const pb = new PocketBase(PB_URL);

// Disable auto-cancellation of overlapping requests by default. Useful when
// the same page issues multiple parallel queries (e.g., a list + an admin
// stats call) that we don't want PB to cancel as duplicates.
pb.autoCancellation(false);

// Convenience typed accessors for the custom user fields we added in the
// initial-schema migration (unit_number, role).
export interface SunsetHarborUserRecord {
  id: string;
  email: string;
  name: string;
  unit_number: string | null;
  /**
   * Extra units this one account also owns, beyond unit_number. Most owners
   * have one unit and this is null/empty. A few owners hold two units and log
   * in with a single account (PocketBase enforces a unique email, so a second
   * per-unit account is not possible). unit_number stays the primary; the
   * additional ones live here.
   */
  additional_units: string[] | null;
  role: 'owner' | 'admin' | 'super_admin';
  must_change_password: boolean;
  verified: boolean;
  created: string;
  updated: string;
}

/**
 * The full list of units an owner holds: the primary unit_number first, then
 * any additional_units, de-duplicated and blank-filtered. Single-unit owners
 * return exactly one entry, so existing behaviour is unchanged for them.
 */
export function ownerUnits(
  u: Pick<SunsetHarborUserRecord, 'unit_number' | 'additional_units'> | null | undefined,
): string[] {
  if (!u) return [];
  const primary =
    u.unit_number != null && String(u.unit_number).trim() !== ''
      ? [String(u.unit_number).trim()]
      : [];
  const extra = Array.isArray(u.additional_units)
    ? u.additional_units.map((x) => String(x).trim()).filter((x) => x !== '')
    : [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of [...primary, ...extra]) {
    if (!seen.has(x)) {
      seen.add(x);
      out.push(x);
    }
  }
  return out;
}

/** Join units for display: ["302"] -> "302", ["302","503"] -> "302 and 503". */
export function formatUnitList(units: string[]): string {
  if (units.length === 0) return '';
  if (units.length === 1) return units[0] as string;
  if (units.length === 2) return `${units[0]} and ${units[1]}`;
  return `${units.slice(0, -1).join(', ')}, and ${units[units.length - 1]}`;
}

/** "Unit 302" for one, "Units 302 and 503" for several, "" for none. */
export function formatUnitLabel(units: string[]): string {
  if (units.length === 0) return '';
  return `${units.length === 1 ? 'Unit' : 'Units'} ${formatUnitList(units)}`;
}

export function currentUser(): SunsetHarborUserRecord | null {
  const model = pb.authStore.model;
  if (!model) return null;
  // The auth model from PB SDK is `Record | Admin`; narrow to our user shape.
  return model as unknown as SunsetHarborUserRecord;
}

export function isAuthenticated(): boolean {
  return pb.authStore.isValid;
}

export function isAdmin(): boolean {
  const u = currentUser();
  return u?.role === 'admin' || u?.role === 'super_admin';
}

export function isSuperAdmin(): boolean {
  return currentUser()?.role === 'super_admin';
}
